/**
 * SVG Sanitizer Script
 *
 * This script provides SVG content sanitization through the SvgSanitizer class.
 * It is designed to sanitize SVG content strings by removing potentially harmful
 * or malicious tags and attributes.
 *
 * It strictly disallows a predefined list of tags and attributes that can be exploited
 * for malicious purposes such as Cross-Site Scripting (XSS) attacks. Any occurrence of
 * blocked tags and attributes within the provided SVG content is removed during the
 * sanitization process.
 *
 * Example Usage:
 * const sanitizedSVG = SvgSanitizer.sanitize('<svg><script>alert("XSS")</script></svg>');
 *
 * Upon finding and removing any blocked tags or attributes, the sanitizer logs a warning
 * message to the console for visibility.
 */
const BLOCKED_TAGS = [
    "base",
    "embed",
    "form",
    "frame",
    "iframe",
    "link",
    "meta",
    "object",
    "script",
    "style"
];
const BLOCKED_ATTRS = [
    "data",
    "formaction",
    "onclick",
    "onerror",
    "onload",
    "onmouseover",
    "src"
];
export class SvgSanitizer {
    /**
     * Sanitizes SVG content by removing potentially harmful elements and attributes.
     *
     * @param {string} svgString - The original SVG content string.
     * @returns {string} - Sanitized SVG content string.
     */
    static sanitize(svgString) {
        if (!this.isWellFormedSVG(svgString)) {
            return ""; // Return empty string if SVG is not well-formed
        }
        try {
            const doc = this.parser.parseFromString(svgString, "image/svg+xml");
            this.removeBlockedElementsAndAttributes(doc, BLOCKED_TAGS, BLOCKED_ATTRS);
            return new XMLSerializer().serializeToString(doc);
        }
        catch (error) {
            console.error("SVG parsing error", error);
            return "";
        }
    }
    /**
     * Checks if the SVG content appears well-formed.
     */
    static isWellFormedSVG(svgString) {
        try {
            const doc = this.parser.parseFromString(svgString, "image/svg+xml");
            const rootElement = doc.documentElement;
            return rootElement && rootElement.tagName.toLowerCase() === "svg";
        }
        catch (error) {
            console.error("Error while validating SVG:", error);
            return false;
        }
    }
    /**
     * Removes blocked elements and attributes from the SVG document.
     */
    static removeBlockedElementsAndAttributes(doc, blockedTags, blockedAttrs) {
        doc.querySelectorAll("*").forEach((element) => {
            var _a;
            const tagName = element.tagName.toLowerCase();
            if (blockedTags.includes(tagName)) {
                console.warn(`Custom SVG Sanitizer: Found and removed blocked tag: <${tagName}>`);
                (_a = element.parentNode) === null || _a === void 0 ? void 0 : _a.removeChild(element);
            }
            else {
                element.getAttributeNames().forEach((attr) => {
                    if (blockedAttrs.includes(attr.toLowerCase()) ||
                        /^on[a-z]+/.test(attr.toLowerCase())) {
                        console.warn(`Custom SVG Sanitizer: Found and removed blocked attribute: ${attr} from <${element.tagName.toLowerCase()}>`);
                        element.removeAttribute(attr);
                    }
                });
            }
        });
    }
}
SvgSanitizer.parser = new DOMParser();
// For backward compatibility and easier migration
export const sanitizeSVG = SvgSanitizer.sanitize.bind(SvgSanitizer);
//# sourceMappingURL=svg-sanitizer.js.map